import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { paths } from './autogen/schema';
import type { HttpMethod, RequestBody, ResponseBody, PathParams, QueryParams } from './helpers';
import { refreshTokens, isTokenExpired } from '@/shared/lib/auth';

if (!import.meta.env.VITE_API_URL) {
    throw new Error('envファイルにVITE_API_URLが設定されてません。.envファイルを確認してください');
}

const axiosConfig = {
    headers: {
        Accept: 'application/json',
    },
    baseURL: import.meta.env.VITE_API_URL,
};

export const axiosInstance = axios.create(axiosConfig);

type AuthContext = {
    getAccessToken: () => string | null;
    getRefreshToken: () => string | null;
    updateTokens: (tokens: { accessToken: string; idToken: string }) => void;
    clearAuth: () => void;
};

export const setupAuthInterceptor = (auth: AuthContext): void => {
    let refreshPromise: Promise<boolean> | null = null;

    // リクエストインターセプター：アクセストークンを自動付与
    axiosInstance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            const accessToken = auth.getAccessToken();
            if (accessToken && !isTokenExpired(accessToken)) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // レスポンスインターセプター：401エラー時の自動リフレッシュ
    axiosInstance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error) => {
            const originalRequest = error.config;

            if (error.response?.status !== 401 || originalRequest._retry) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;
            const refreshToken = auth.getRefreshToken();

            if (!refreshToken) {
                return Promise.reject(error);
            }

            if (refreshPromise) {
                const success = await refreshPromise;
                if (success) {
                    originalRequest.headers.Authorization = `Bearer ${auth.getAccessToken()}`;
                    return axiosInstance(originalRequest);
                }
                return Promise.reject(error);
            }

            refreshPromise = (async () => {
                const result = await refreshTokens(refreshToken);
                if (result.ok) {
                    auth.updateTokens({
                        accessToken: result.tokens.access_token,
                        idToken: result.tokens.id_token,
                    });
                    return true;
                }
                auth.clearAuth();
                return false;
            })();

            try {
                const success = await refreshPromise;
                if (success) {
                    originalRequest.headers.Authorization = `Bearer ${auth.getAccessToken()}`;
                    return axiosInstance(originalRequest);
                }
                return Promise.reject(error);
            } finally {
                refreshPromise = null;
            }
        }
    );
};

type PathsWithMethod<M extends HttpMethod> = {
    [P in keyof paths]: M extends keyof paths[P] ? P : never;
}[keyof paths];

type RequestOptions<P extends keyof paths, M extends HttpMethod> = (PathParams<P, M> extends never
    ? { path?: never }
    : { path: PathParams<P, M> }) &
    (QueryParams<P, M> extends never ? { query?: never } : { query?: QueryParams<P, M> }) &
    (RequestBody<P, M> extends never ? { body?: never } : { body: RequestBody<P, M> });

type SuccessResponse<P extends keyof paths, M extends HttpMethod> =
    | ResponseBody<P, M, 200>
    | ResponseBody<P, M, 201>
    | ResponseBody<P, M, 204>;

const buildUrl = <P extends keyof paths>(path: P, pathParams?: Record<string, string>): string => {
    let url = path as string;
    if (pathParams) {
        for (const [key, value] of Object.entries(pathParams)) {
            url = url.replace(`{${key}}`, encodeURIComponent(value));
        }
    }
    return url;
};

const request = async <P extends keyof paths, M extends HttpMethod>(
    method: M,
    path: P,
    options?: RequestOptions<P, M>
): Promise<SuccessResponse<P, M>> => {
    const url = buildUrl(path, options?.path as Record<string, string> | undefined);

    const response = await axiosInstance.request({
        method,
        url,
        params: options?.query,
        data: options?.body,
    });

    return response.data;
};

export const api = {
    get: <P extends PathsWithMethod<'get'>>(path: P, options?: RequestOptions<P, 'get'>) =>
        request('get', path, options),

    post: <P extends PathsWithMethod<'post'>>(path: P, options?: RequestOptions<P, 'post'>) =>
        request('post', path, options),

    put: <P extends PathsWithMethod<'put'>>(path: P, options?: RequestOptions<P, 'put'>) =>
        request('put', path, options),

    delete: <P extends PathsWithMethod<'delete'>>(path: P, options?: RequestOptions<P, 'delete'>) =>
        request('delete', path, options),

    patch: <P extends PathsWithMethod<'patch'>>(path: P, options?: RequestOptions<P, 'patch'>) =>
        request('patch', path, options),

    postFormData: async <P extends PathsWithMethod<'post'>>(
        path: P,
        options: { path?: Record<string, string>; formData: FormData }
    ): Promise<SuccessResponse<P, 'post'>> => {
        const url = buildUrl(path, options.path);
        const response = await axiosInstance.post(url, options.formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    getBlob: async <P extends PathsWithMethod<'get'>>(
        path: P,
        options?: { path?: Record<string, string>; query?: Record<string, unknown> }
    ): Promise<Blob> => {
        const url = buildUrl(path, options?.path);
        const response = await axiosInstance.get(url, {
            params: options?.query,
            responseType: 'blob',
        });
        return response.data;
    },
};
